import { Field, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import {
  Button,
  Form,
  FormGroup,
  InlineLoading,
  InlineNotification,
  Select,
  SelectItem,
  Stack,
  TextArea,
  TextInput,
} from '@carbon/react';
import { FormikTextInput } from 'src/components/Form/FormikInputs';
import { timesheetErrorKey } from 'src/components/Timesheet/timesheetFormat';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant, useUpdateRestaurantMutation } from 'src/hooks/useRestaurants';
import { useTimesheetHolidayActions, useTimesheetHolidays } from 'src/hooks/useTimesheets';
import { useTransactionCategories } from 'src/hooks/useTransactionCategories';
import { TransactionCategoryTypeEnum } from 'src/utils/accounting';

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const num = (min: number, max: number) => z.preprocess((v) => parseFloat(`${v}`), z.number().min(min).max(max));
// parseFloat, not Number: Number('') is 0 (silently valid), while parseFloat('') is NaN and
// fails type validation — same empty-string behavior as num() above.
const whole = (max: number) => z.preprocess((v) => parseFloat(`${v}`), z.number().int().min(0).max(max));

// Single source of truth for turning the textarea's raw text into the array the server
// receives — used by both validation and onSubmit so they cannot drift apart.
const parseShiftNames = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

// Mirrors the server's RestaurantCreateDto bounds (Task 4 Step 7): ArrayMaxSize(20) +
// MaxLength(60, { each: true }) on timesheetShiftNames.
const Schema = z.object({
  timesheetPeriod: z.enum(['MONTH', 'SEMI_MONTH', 'WEEK']),
  timesheetWeekendMultiplier: num(1, 5),
  timesheetHolidayMultiplier: num(1, 5),
  timesheetNightPremium: num(0, 2),
  timesheetNightStart: z.string().regex(HHMM),
  timesheetNightEnd: z.string().regex(HHMM),
  timesheetGeofenceMeters: z.preprocess((v) => parseInt(`${v}`, 10), z.number().int().min(0).max(5000)),
  shiftNamesText: z
    .string()
    .max(20 * 61)
    .refine((text) => {
      const names = parseShiftNames(text);
      return names.length <= 20 && names.every((name) => name.length <= 60);
    }, 'timesheet.settings.shiftNamesInvalid'),
  // Formik's prepareDataForValidation turns '' into undefined before validating (Yup-oriented
  // behavior baked into Formik core), and '' is the legitimate "no category" state here.
  timesheetExpenseCategoryId: z.string().optional(),
  // Scheduling (spec 2026-09-27 §7.3): whole minutes/hours, matching the server's bounds.
  scheduleMatchWindowMin: whole(240),
  scheduleLateGraceMin: whole(120),
  scheduleCancelCutoffHours: whole(168),
  scheduleReminderLeadHours: whole(48),
});

function Holidays() {
  const { t } = useTranslation();
  const { data: holidays } = useTimesheetHolidays();
  const { create, remove, loadVn } = useTimesheetHolidayActions();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const year = new Date().getFullYear();
  const error = create.error ?? remove.error ?? loadVn.error;

  return (
    <FormGroup legendText={t('timesheet.settings.holidays')}>
      <Stack gap={4}>
        {(holidays ?? []).map((holiday) => (
          <div key={holiday.id} className="timesheet-settings__holiday">
            <span>{holiday.date}</span>
            <span>{holiday.name}</span>
            <Button size="sm" kind="danger--ghost" onClick={() => remove.mutate(holiday.id)}>
              {t('timesheet.actions.delete')}
            </Button>
          </div>
        ))}
        <div className="timesheet-settings__holiday">
          <TextInput
            id="holiday-date"
            type="date"
            labelText={t('timesheet.columns.date')}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <TextInput
            id="holiday-name"
            labelText={t('timesheet.settings.holidayName')}
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
          />
          <Button
            size="md"
            disabled={!date || !name.trim() || create.isLoading}
            onClick={() =>
              create.mutate(
                { date, name: name.trim() },
                {
                  onSuccess: () => {
                    setDate('');
                    setName('');
                  },
                }
              )
            }
          >
            {t('timesheet.settings.addHoliday')}
          </Button>
        </div>
        <div className="timesheet-settings__holiday">
          {[year, year + 1].map((y) => (
            <Button
              key={y}
              kind="tertiary"
              data-testid={`timesheet-load-vn-${y}`}
              disabled={loadVn.isLoading}
              onClick={() => loadVn.mutate(y)}
            >
              {t('timesheet.settings.loadVn', { year: y })}
            </Button>
          ))}
        </div>
        {error && <InlineNotification kind="error" lowContrast hideCloseButton title={t(timesheetErrorKey(error))} />}
      </Stack>
    </FormGroup>
  );
}

const TimesheetSettings = () => {
  const { t } = useTranslation();
  const restaurant = useCurrentRestaurant();
  const { data: branches } = useBranches();
  const { data: categories } = useTransactionCategories();
  const update = useUpdateRestaurantMutation();

  if (!restaurant) return <InlineLoading />;

  const expenseCategories = (categories?.data ?? []).filter((c) => c.type === TransactionCategoryTypeEnum.EXPENSE);
  const branchesWithoutCoords = (branches?.data ?? []).filter((b) => b.latitude == null || b.longitude == null);

  return (
    <Form>
      <Formik
        validationSchema={toFormikValidationSchema(Schema)}
        initialValues={{
          timesheetPeriod: restaurant.timesheetPeriod,
          timesheetWeekendMultiplier: `${restaurant.timesheetWeekendMultiplier}`,
          timesheetHolidayMultiplier: `${restaurant.timesheetHolidayMultiplier}`,
          timesheetNightPremium: `${restaurant.timesheetNightPremium}`,
          timesheetNightStart: restaurant.timesheetNightStart,
          timesheetNightEnd: restaurant.timesheetNightEnd,
          timesheetGeofenceMeters: `${restaurant.timesheetGeofenceMeters}`,
          shiftNamesText: (restaurant.timesheetShiftNames ?? []).join('\n'),
          timesheetExpenseCategoryId: restaurant.timesheetExpenseCategoryId ?? '',
          scheduleMatchWindowMin: `${restaurant.scheduleMatchWindowMin ?? 60}`,
          scheduleLateGraceMin: `${restaurant.scheduleLateGraceMin ?? 10}`,
          scheduleCancelCutoffHours: `${restaurant.scheduleCancelCutoffHours ?? 24}`,
          scheduleReminderLeadHours: `${restaurant.scheduleReminderLeadHours ?? 3}`,
        }}
        onSubmit={(values) =>
          update.mutate({
            id: restaurant.id,
            name: restaurant.name,
            slug: restaurant.slug,
            timesheetPeriod: values.timesheetPeriod,
            timesheetWeekendMultiplier: parseFloat(values.timesheetWeekendMultiplier),
            timesheetHolidayMultiplier: parseFloat(values.timesheetHolidayMultiplier),
            timesheetNightPremium: parseFloat(values.timesheetNightPremium),
            timesheetNightStart: values.timesheetNightStart,
            timesheetNightEnd: values.timesheetNightEnd,
            timesheetGeofenceMeters: parseInt(values.timesheetGeofenceMeters, 10),
            timesheetShiftNames: parseShiftNames(values.shiftNamesText),
            timesheetExpenseCategoryId: values.timesheetExpenseCategoryId || null,
            scheduleMatchWindowMin: Number(values.scheduleMatchWindowMin),
            scheduleLateGraceMin: Number(values.scheduleLateGraceMin),
            scheduleCancelCutoffHours: Number(values.scheduleCancelCutoffHours),
            scheduleReminderLeadHours: Number(values.scheduleReminderLeadHours),
          } as never)
        }
      >
        {({ handleSubmit, values, setFieldValue, isValid }) => (
          <FormGroup legendText={t('timesheet.settings.title')}>
            <Stack gap={6}>
              <Select
                id="timesheetPeriod"
                labelText={t('timesheet.settings.period')}
                value={values.timesheetPeriod}
                onChange={(e) => setFieldValue('timesheetPeriod', e.target.value)}
              >
                {(['MONTH', 'SEMI_MONTH', 'WEEK'] as const).map((p) => (
                  <SelectItem key={p} value={p} text={t(`timesheet.settings.periods.${p}`)} />
                ))}
              </Select>
              <FormikTextInput name="timesheetWeekendMultiplier" label={t('timesheet.settings.weekend')} />
              <FormikTextInput name="timesheetHolidayMultiplier" label={t('timesheet.settings.holiday')} />
              <FormikTextInput name="timesheetNightPremium" label={t('timesheet.settings.nightPremium')} />
              <div style={{ display: 'flex', gap: '1rem' }}>
                <FormikTextInput name="timesheetNightStart" label={t('timesheet.settings.nightStart')} />
                <FormikTextInput name="timesheetNightEnd" label={t('timesheet.settings.nightEnd')} />
              </div>
              <FormikTextInput
                name="timesheetGeofenceMeters"
                label={t('timesheet.settings.geofence')}
                helperText={t('timesheet.settings.geofenceHint')}
              />
              {values.timesheetGeofenceMeters !== '0' &&
                branchesWithoutCoords.map((b) => (
                  <InlineNotification
                    key={b.id}
                    data-testid="timesheet-settings-no-coords"
                    kind="warning"
                    lowContrast
                    hideCloseButton
                    title={t('timesheet.settings.noCoordinates', { name: b.name })}
                  />
                ))}
              <Field name="shiftNamesText">
                {({ field, meta }) => (
                  <TextArea
                    id="shiftNamesText"
                    labelText={t('timesheet.settings.shiftNames')}
                    helperText={t('timesheet.settings.shiftNamesHint')}
                    rows={4}
                    invalid={!!(meta.touched && meta.error)}
                    invalidText={meta.error ? t(meta.error) : undefined}
                    {...field}
                  />
                )}
              </Field>
              <Select
                id="timesheetExpenseCategoryId"
                labelText={t('timesheet.settings.expenseCategory')}
                value={values.timesheetExpenseCategoryId}
                onChange={(e) => setFieldValue('timesheetExpenseCategoryId', e.target.value)}
              >
                <SelectItem value="" text="—" />
                {expenseCategories.map((c) => (
                  <SelectItem key={c.id} value={c.id} text={c.name} />
                ))}
              </Select>
              <FormGroup legendText={t('schedule.settings.title')}>
                <Stack gap={6}>
                  <FormikTextInput name="scheduleMatchWindowMin" label={t('schedule.settings.matchWindow')} />
                  <FormikTextInput name="scheduleLateGraceMin" label={t('schedule.settings.lateGrace')} />
                  <FormikTextInput name="scheduleCancelCutoffHours" label={t('schedule.settings.cancelCutoff')} />
                  <FormikTextInput name="scheduleReminderLeadHours" label={t('schedule.settings.reminderLead')} />
                </Stack>
              </FormGroup>
              {update.error && (
                <InlineNotification
                  kind="error"
                  lowContrast
                  hideCloseButton
                  title={t(timesheetErrorKey(update.error))}
                />
              )}
              {update.isLoading || update.isSuccess ? (
                <InlineLoading
                  status={update.isSuccess ? 'finished' : 'active'}
                  description={t(update.isSuccess ? 'common.saved' : 'common.saving')}
                />
              ) : (
                <Button data-testid="timesheet-settings-save" disabled={!isValid} onClick={() => handleSubmit()}>
                  {t('actions.save')}
                </Button>
              )}
            </Stack>
          </FormGroup>
        )}
      </Formik>
      <Holidays />
    </Form>
  );
};

export default TimesheetSettings;
